import { removeItem } from "./index.js";
import { getAllDescendants, getTestNode } from "./testSelection.js";

export function handleDuplicateTests(selectProp, supportObj) {
  // check for package tests with bullet list expand icon
  let { testNodeList, selectedTestIdList } = supportObj;
  handleBulletListPackageTests(testNodeList, selectedTestIdList);
  // check for duplicate tests with string ids
  handleStringIdTests(selectProp, supportObj);
}

/***
 * check for 2 package tests: Comprehensive Metabolic Panel (id: 99), Basic Metabolic Panel (id: 98)
 * if package test selected, and any of tests in these packages are selected in selected lists, remove
 * temporarily add new test: Food Allergen Panel (id: 727)
 * ****************************************************
 * 01/29/2025: add new test: autoimmune zoomer (id: 830), temporarily hardcode its descendant test ids
 * for hs-crp (VP-8682)
 */

export function handleBulletListPackageTests(testNodeList, selectedTestIdList) {
  let specialPackageNodes = testNodeList.filter(node => [98, 99, 727, 830].includes(node.id));
  specialPackageNodes.forEach(node => {
    // get test ids included in current package node
    let testIds = node.id === 830 ? [339] : node.value.packageTests.map(test => test.id);
    // let testNodes = testNodeList.filter(node => testIds.includes(node.id) || testIds.includes(node.value.originalId));
    if (selectedTestIdList.includes(node.id)) {
      // if any of the ids in selected test id list, remove
      [...selectedTestIdList].forEach(id => {
        let integerId = typeof id === "string" ? Number(id.split("_")[0]) : id;
        if (testIds.includes(integerId)) {
          removeItem(id, selectedTestIdList);
        }
      });
    }
  });
}

// check whether any selected duplicate tests included in other selected test/bundle
// if so, remove this test from selectedTestIdList
function handleStringIdTests(selectProp, supportObj) {
  // get all tests with string id
  let { testNodeList, selectedTestIdList } = supportObj;
  let stringIdNodes = testNodeList.filter(node => typeof node.id === "string");
  // get integer ids for duplicate tests
  let integerIds = stringIdNodes.map(node => node.value.originalId);
  integerIds = [...new Set(integerIds)];
  // for each integer id, find its related selected string ids/testNode
  integerIds.forEach(integerId => {
    let selectedDuplicateNodes = stringIdNodes.filter(node => node.value.originalId === integerId && node.value[selectProp]);
    let selectedDuplicateIds = selectedDuplicateNodes.map(node => node.id);
    selectedDuplicateNodes.forEach(node => {
      // if any duplicate belong to other selected package or panel
      // remove it from selectedTestIdList
      if (isBelongToSelectedPackageOrPanel(node.id, selectProp, supportObj) || isBelongToEditOrder(node.id, supportObj)) {
        selectedDuplicateIds.forEach(id => {
          removeItem(id, selectedTestIdList);
        });
      }
      else {
        // test is select, but not in selectedTestList
        // check whether add it to list        
        if (!selectedTestIdList.includes(node.id)) {
          // check whether any other duplicate test is in selectedTestList already
          // if no, add it to selectedTestList
          let isSelectedAlready = otherDuplicteTestIsSelected(integerId, selectedTestIdList);
          if (!isSelectedAlready) {
            selectedTestIdList.push(node.id);
          }
        }
      }
    })
  });
}

// exclude xChooseY panels, since they do not contain any string id tests at this moment
function isBelongToSelectedPackageOrPanel(testId, selectProp, supportObj) {
  let isBelongTo = false;
  let { 
    selectedTestIdList, 
    selectedDiscountPanelIdList, 
    selectedCustomPanelIdList, 
    selectedShortcutIdList, 
    testNodeList, 
    bundleList, 
    shortcutList 
  } = supportObj;

  // check for selected tests
  let selectedTestNodes = testNodeList.filter(node => selectedTestIdList.includes(node.id));
  selectedTestNodes.forEach(node => {
    if (node.id !== testId) {
      if (isBelongToCurrentTestNode(node, testId)) {
        isBelongTo = true;
      }
    }
  });

  // check for selected discount panels
  let selectedDiscountPanels = bundleList.filter(bundle => selectedDiscountPanelIdList.includes(bundle.bundleId));
  selectedDiscountPanels.forEach(panel => {
    if (isBelongToCurrentDiscountPanel(panel, testId, supportObj)) {
      isBelongTo = true;
    }
  });

  // check for selected custom panels
  let selectedCustomPanels = bundleList.filter(bundle => selectedCustomPanelIdList.includes(bundle.bundleId));
  selectedCustomPanels.forEach(panel => {
    if (isBelongToCurrentCustomPanel(panel, testId, supportObj)) {
      isBelongTo = true;
    }
  });

  // check for selected shortcuts
  let selectedShortcuts = shortcutList.filter(shortcut => selectedShortcutIdList.includes(shortcut.id));
  selectedShortcuts.forEach(shortcut => {
    let testNodes = testNodeList.filter(node => shortcut.testIdList.includes(node.id));
    testNodes.forEach(node => {
      if (node.id === testId || isBelongToCurrentTestNode(node, testId)) {
        isBelongTo = true;
      }
    });
    let discountPanels = bundleList.filter(bundle => shortcut.discountPanelIdList.includes(bundle.bundleId));
    discountPanels.forEach(panel => {
      if (isBelongToCurrentDiscountPanel(panel, testId, supportObj)) {
        isBelongTo = true;
      }
    });
    let customPanels = bundleList.filter(bundle => shortcut.customPanelIdList.includes(bundle.bundleId));
    customPanels.forEach(panel => {
      if (isBelongToCurrentCustomPanel(panel, testId, supportObj)) {
        isBelongTo = true;
      }
    });
    if (typeof testId === "string" && shortcut.testIdList.includes(Number(testId.split("_")[0]))) {
      isBelongTo = true;
    }
  })

  // check for selected bullet list tests
  let specialPackageNodes = testNodeList.filter(node => [98, 99, 727, 830].includes(node.id));
  specialPackageNodes.filter(node => node.value[selectProp]).forEach(node => {
    let testIds = node.id === 830 ? [339] : node.value.packageTests.map(test => test.id);
    if (testIds.includes(testId)) {
      isBelongTo = true;
    }
    if (typeof testId === "string" && testIds.includes(Number(testId.split("_")[0]))) {
      isBelongTo = true;
    }
  });
  return isBelongTo;
}

function isBelongToEditOrder(testId, supportObj) {
  let isBelongTo = false;
  let { editOrderItemList, testNodeList } = supportObj;
  let testIdIntegerPart = Number(testId.split("_")[0]);
  editOrderItemList.filter(item => ["va", "vw"].includes(item.category)).forEach(test => {
    let editOrderItemIdIntegerPart = isNaN(test.item_id) ? Number(test.item_id.split("_")[0]) : test.item_id;
    if (testIdIntegerPart === editOrderItemIdIntegerPart) { // given testId itself belong to edit order
      isBelongTo = true;
    } else { // given testId belong to other tests selcted in edit order
      let testOnMenu = testNodeList.find(node => node.id === test.item_id);
      if (isBelongToCurrentTestNode(testOnMenu, testId)) {
        isBelongTo = true;
      }
    }
  });
  editOrderItemList.filter(item => item.category === "shortcut").forEach(shortcut => {
    let testsInShortcut = shortcut.contents.filter(c => c.item_type === "packagePriceId");
    testsInShortcut.forEach(test => {
      let testOnMenu = testNodeList.find(node => node.id === test.item_id);
      if (testOnMenu && (testOnMenu.id === testId || isBelongToCurrentTestNode(testOnMenu, testId))) {
        isBelongTo = true;
      }
    });
    // TODO: handle for discount panel, custom bundle in shortcut
  })
  // TODO: handle for rest bundles
  return isBelongTo;
}

function isBelongToCurrentTestNode(node, testId) {
  let descendantList = [];
  getAllDescendants(node, descendantList);
  if (descendantList.map(node => node.id).includes(testId)) {
    return true;
  }
  // also check whether node contains any duplicate of testId
  let integerPart = Number(testId.split("_")[0]);
  let stringIdDescendants = descendantList.filter(d => isNaN(d.id));
  let containsDuplicate = stringIdDescendants.find(d => d.value.originalId === integerPart);
  return containsDuplicate ? true : false;
}

function isBelongToCurrentDiscountPanel(panel, testId, supportObj) {
  let { testNodeList } = supportObj;
  let idList = panel.bundleContent.packageList.map(p => p.packageId)
    .concat(panel.bundleContent.testList.map(t => t.testId));
  idList.forEach(id => {
    let currentNode = getTestNode(testNodeList, id);
    let descendantList = [];
    getAllDescendants(currentNode, descendantList);
    idList = idList.concat(descendantList.map(d => d.id));
  });
  if (idList.includes(testId)) {
    return true;
  }
  if (typeof testId === "string" && idList.includes(Number(testId.split("_")[0]))) {
    return true;
  }
  return false;
}

function isBelongToCurrentCustomPanel(panel, testId, supportObj) {
  let { bundleList, testNodeList } = supportObj;
  let idList = [];
  panel.bundleItemList.forEach(content => {
    if (content.itemType === "official bundle") {
      if (content.itemId) {
        let item = bundleList.filter(b => b.bundleId === content.itemId);
        let testIds = item.bundleContent.packageList.map(p => p.packageId)
          .concat(item.bundleContent.testList.map(t => t.testId));
      idList.push(testIds);
      }
    } 
    else { // test
      if (content.itemId) {
        let currentNode = getTestNode(testNodeList, content.itemId);
        if (currentNode) {
          let descendantList = [];
          getAllDescendants(currentNode, descendantList);
          idList.push(currentNode.id);
          idList = idList.concat(descendantList.map(d => d.id));
        }
      }
    }
  });
  if (idList.includes(testId)) {
    return true;
  }
  if (typeof testId === "string" && idList.includes(Number(testId.split("_")[0]))) {
    return true;
  }
  return false;
}

function otherDuplicteTestIsSelected(integerId, selectedTestIdList) {
  let possibleSelectedDuplicateTest = selectedTestIdList.find(id => {
    return isNaN(id) && id.split("_")[0] === integerId.toString();
  });
  return possibleSelectedDuplicateTest ? true : false;
}